@use 'sass:map';
@use '../../scss/utility';
@use '../grey-box/grey-box-vars';

$label-height: map.get(utility.$TEXT_CONFIGS, 'l', 'mq1', 'line-height');
$floating-label-height: map.get(utility.$TEXT_CONFIGS, 's', 'mq1', 'line-height');
$input-text-height: map.get(utility.$TEXT_CONFIGS, 'l', 'mq1', 'line-height');
$label-height-s: map.get(utility.$TEXT_CONFIGS, 'm', 'mq1', 'line-height');
$input-text-height-s: map.get(utility.$TEXT_CONFIGS, 'm', 'mq1', 'line-height');

:host {
  --input-actions-width: 0px; // set via JS to preserve space for passed in wb-input-action elements

  display: block;
  overflow: hidden;

  .wb-grey-box {
    --label-height: #{$label-height};
    --floating-label-height: #{$floating-label-height};
    --input-text-height: #{$input-text-height};
    --block-remaining-space: calc(var(--grey-box-height) - (var(--floating-label-height) + var(--input-text-height) + var(--grey-box-label-text-gap)));
  }
}

:host([size='s']:not([as='textarea'])) {
  @include grey-box-vars.wb-grey-box-s-hint-vars;

  .wb-grey-box {
    --label-height: #{$label-height-s};
    --input-text-height: #{$input-text-height-s};
  }

  label {
    @include utility.wb-text(
      'm',
      (
        responsive: false,
      )
    );
  }

  input,
  textarea,
  .textarea-wrap {
    @include utility.wb-text(
      'm',
      (
        responsive: false,
      )
    );
  }
}

:host([size='m']:not([as='textarea'])) {
  @include grey-box-vars.wb-grey-box-m-hint-vars;
}

:host([size='l']),
:host([as='textarea']) {
  @include grey-box-vars.wb-grey-box-l-hint-vars;
}

$status-width: calc(var(--grey-box-status-padding) + var(--wb-status-badge-size) + var(--grey-box-status-padding) + var(--grey-box-padding-inline-end));

:host([success]),
:host([error]) {
  label {
    inset-inline-end: calc(#{$status-width} + var(--input-actions-width));
  }
  input,
  textarea,
  .textarea-wrap {
    padding-inline-end: $status-width;
  }
}

:host([type='password']),
:host([type='date']) {
  label {
    inset-inline-end: calc(var(--grey-box-action-size) + var(--input-actions-width));
  }

  input {
    padding-inline-end: var(--grey-box-action-size);
  }
}

:host([type='password'][error]),
:host([type='password'][success]),
:host([type='date'][error]),
:host([type='date'][success]) {
  label {
    inset-inline-end: calc(#{$status-width} + var(--grey-box-action-size) + var(--input-actions-width));
  }

  input {
    padding-inline-end: calc(#{$status-width} + var(--grey-box-action-size));
  }

  input::-webkit-calendar-picker-indicator {
    inset-inline-end: $status-width;
  }

  .special-action-button {
    inset-inline-end: $status-width;
  }
}

slot[name='leading-icon']::slotted(.wb-icon) {
  color: var(--grey-box-leading-icon-color);
  --size: var(--grey-box-leading-icon-size);
  margin-inline-start: var(--grey-box-padding-inline-start);
  flex-shrink: 0;
}

.special-action-button {
  position: absolute;
  inset-inline-end: var(--grey-box-padding-inline-end);
}

// modifier used to "disable" special action icons when the input is disabled
// or the native datepicker should open when clicked through
.is-click-through {
  pointer-events: none;
}

label {
  position: absolute;
  inset-block-start: calc((var(--grey-box-height) - var(--label-height)) / 2);
  inset-inline: var(--grey-box-padding-inline-start) calc(var(--grey-box-padding-inline-end) + var(--input-actions-width));
  transition: inset-block-start 0.2s var(--wb-move);
  @include utility.wb-text(
    'l',
    (
      responsive: false,
    )
  );
  cursor: text;
  color: var(--grey-box-label-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.has-leading-icon {
  label {
    inset-inline-start: calc(var(--grey-box-padding-inline-start) + var(--grey-box-leading-icon-size) + var(--grey-box-start-gap));
  }

  input,
  textarea,
  .textarea-wrap {
    padding-inline-start: var(--grey-box-start-gap);
  }
}

:host .has-floating-label {
  input {
    opacity: 1;
  }

  textarea {
    opacity: 1;
    resize: vertical;
  }

  label.label {
    @include grey-box-vars.wb-grey-box-floating-label-vars;
    inset-block-start: calc(var(--block-remaining-space) / 2);
    @include utility.wb-text(
      's',
      (
        responsive: false,
      )
    );
  }
}

input,
textarea {
  color: var(--grey-box-input-color);
  margin: 0;
  outline: none;
  border: none;
  background-color: transparent;
  text-overflow: ellipsis;
  opacity: 0;
  flex: 1;
  align-self: flex-end;
  box-sizing: border-box;

  :-webkit-autofill {
    background-clip: text;
    -webkit-text-fill-color: var(--grey-box-input-color);
  }
}

input,
textarea,
.textarea-wrap {
  @include utility.wb-text(
    'l',
    (
      responsive: false,
    )
  );
  padding-block-start: calc(var(--block-remaining-space) / 2 + #{$floating-label-height});
  padding-block-end: calc(var(--block-remaining-space) / 2);
  padding-inline: var(--grey-box-padding-inline-start) var(--grey-box-padding-inline-end);
}

input::-webkit-date-and-time-value {
  text-align: start;
}

@supports (-webkit-touch-callout: none) {
  /* CSS specific to iOS devices */
  input,
  textarea {
    flex: 0 0 1; // setting to `flex-basis: 0` fixed "shrinking" problem mentioned in issue #1557
  }
}

input {
  text-align: start;
  min-width: 0;
}

input::placeholder {
  color: var(--grey-box-placeholder-color);
}

input::-webkit-datetime-edit {
  color: var(--grey-box-input-color);
}

input::-ms-clear {
  display: none;
}

input::-webkit-clear-button {
  display: none;
}

input::-webkit-calendar-picker-indicator,
input::-webkit-inner-spin-button {
  width: var(--grey-box-action-size);
  height: var(--grey-box-action-size);
  padding: 0;
  margin: 0;
  position: absolute;
  inset-inline-end: var(--grey-box-padding-inline-end);
  inset-block-start: var(--grey-box-padding-block-start);
  background-color: red;
  opacity: 0;
}

input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}

textarea {
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  min-height: utility.wb-pixel-to-rem(106px);
  overflow-x: hidden;
  overflow-y: auto;
  resize: none; // only when active/filled
}

:host([as='textarea']) .status-icon {
  position: absolute;
  inset-inline-end: calc(var(--grey-box-padding-inline-end) + var(--grey-box-status-padding));
  inset-block-start: calc(var(--grey-box-padding-inline-end) + var(--grey-box-status-padding));
  margin: 0 !important;
}

slot[name='message']::slotted(*) {
  margin-inline-start: var(--grey-box-hint-padding-inline-start);
}

::slotted(.wb-input-action:not(:first-of-type)) {
  border-inline-start: var(--grey-box-divider-weight) solid var(--grey-box-divider-color);
  margin-inline-start: var(--grey-box-action-gap);
}

::slotted(.wb-input-action:last-of-type) {
  margin-inline-end: var(--grey-box-padding-inline-end);
}

input,
label {
  -webkit-tap-highlight-color: transparent;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  height: auto; // correct the cursor style of increment and decrement buttons in chrome.
}

input[type='search'] {
  -webkit-appearance: textfield; // correct the odd appearance in chrome and safari.
  outline-offset: utility.wb-pixel-to-rem(-2px); // correct the outline style in safari.
}

input[type='search']::-webkit-search-decoration {
  -webkit-appearance: none; // remove the inner padding in chrome and safari on macos.
}

:host([compact]) {
  display: inline-flex;
  flex-direction: column;
  min-width: utility.wb-pixel-to-rem(144px);

  .has-floating-label {
    label {
      opacity: 0;
      inset-block-start: calc((var(--grey-box-height) - var(--label-height)) / 2);
    }
  }

  input,
  textarea,
  .textarea-wrap {
    padding-block: calc((var(--grey-box-height) - var(--input-text-height)) / 2);
  }
}

/* The auto-grow feature was inspired by https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/ */
:host([auto-grow]) {
  /* Resets for textarea */
  textarea {
    margin: 0;
    padding: 0;
  }

  .textarea-wrap {
    /* Easy way to plop the elements on top of each other and have them both sized based on the tallest one's height */
    display: grid;
    width: 100%;

    overflow: hidden;
  }

  .textarea-wrap::after {
    /* Note the weird space! Needed to prevent jumpy behavior */
    content: attr(data-replicated-value) ' ';

    /* This is how textarea text behaves */
    white-space: pre-wrap;

    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
  }

  .textarea-wrap > textarea {
    /* You could leave this, but after a user resizes, then it ruins the auto sizing */
    resize: none;
  }

  .textarea-wrap > textarea,
  .textarea-wrap::after {
    font: inherit;
    height: 100%;
    min-height: utility.wb-pixel-to-rem(106px);
    overflow: hidden;

    /* Place on top of each other */
    grid-area: 1 / 1 / 2 / 2;
  }
}
